import { ComponentType } from 'react';

import { CompanyCard, SoldToAddressCard } from '../../../../../../components/cards/company';
import { AdditionalAttachmentCard } from '../../../../../../components/cards/financial/AttachmentCard';
import PrivacyPolicyCard from '../../../../../../components/cards/financial/PrivacyPolicyCard';
import {
  CertOfAnalysisContactCard,
  DeliveryDetailsCard,
  DeliveryInstructionsCard,
  OrderConfirmationContactCard
} from '../../../../../../components/cards/shipTo';
import CompanyPage from '../../../../../../features/CompanyPage';
import ReviewPage from '../../../../../../features/ReviewPage';
import { CARD_CATEGORIES } from '../../../../../../shared/cardConfig/constants';
import {
  ATTACHMENT_FIELDS,
  COMPANY_FIELDS,
  CONTACT_FIELDS,
  CONTACT_SAME_AS_FIELDS,
  DELIVERY_DETAILS_FIELDS,
  DELIVERY_INSTRUCTIONS_FIELDS,
  SOLD_TO_ADDRESS_FIELDS
} from '../../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../../utils/constants';

// ----------------- Type Definitions -----------------
interface Field {
  key: string;
  required?: boolean;
}

interface Card {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  fields?: Field[];
  hideSameAs?: boolean;
  fieldWrapper?: string;
  sameAsFields?: string[];
  errorMustContain?: string;
}

interface PageTemplate {
  route: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  cards?: Card[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accordions?: { title: string; cards: Card[] | ((...args: any[]) => Card[]) }[];
}

const contactFields: Field[] = [
  { key: CONTACT_FIELDS.email },
  { key: CONTACT_FIELDS.fax }
];

// ----------------- Page Templates -----------------
const companyPageTemplate: PageTemplate = {
  route: PAGE_ROUTES.company,
  Component: CompanyPage,
  cards: [
    {
      key: CARD_CATEGORIES.company,
      Component: CompanyCard,
      fields: [
        { key: COMPANY_FIELDS.tradeDbaName },
        { key: COMPANY_FIELDS.legalName },
        { key: COMPANY_FIELDS.parentCompany }
      ]
    },
    {
      key: CARD_CATEGORIES.soldTo,
      Component: SoldToAddressCard,
      fields: [
        { key: SOLD_TO_ADDRESS_FIELDS.address },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber1 },
        { key: SOLD_TO_ADDRESS_FIELDS.email, required: true },
        { key: SOLD_TO_ADDRESS_FIELDS.icmsTaxPayer, required: true },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone1, required: true },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone2 },
        { key: SOLD_TO_ADDRESS_FIELDS.xmlEmail },
        { key: SOLD_TO_ADDRESS_FIELDS.paymentSlipEmail }
      ],
      errorMustContain: 'address'
    },
    {
      key: CARD_CATEGORIES.deliveryDetails,
      Component: DeliveryDetailsCard,
      fields: [
        { key: DELIVERY_DETAILS_FIELDS.appointmentRequired },
        { key: DELIVERY_DETAILS_FIELDS.receivingPlantContactName },
        { key: DELIVERY_DETAILS_FIELDS.receivingPlantPhoneNumber },
        { key: DELIVERY_DETAILS_FIELDS.afterHoursContactName },
        { key: DELIVERY_DETAILS_FIELDS.afterHoursPhoneNumber },
        { key: DELIVERY_DETAILS_FIELDS.warehouseReceivingHours }
      ],
      fieldWrapper: `${CARD_CATEGORIES.shipTo}[0]`
    },
    {
      key: CARD_CATEGORIES.deliveryInstructions,
      Component: DeliveryInstructionsCard,
      fields: [
        { key: DELIVERY_INSTRUCTIONS_FIELDS.shippingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.shippingMarks },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.customsText },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.loadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.unloadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.packingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.billOfLadingText },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.textNotaFiscal }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.coa,
      Component: CertOfAnalysisContactCard,
      fields: contactFields,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      sameAsFields: [CONTACT_SAME_AS_FIELDS.sameAsInvoiceDelivery]
    },
    {
      key: CARD_CATEGORIES.orderConfirmation,
      Component: OrderConfirmationContactCard,
      fields: contactFields,
      fieldWrapper: CARD_CATEGORIES.shipTo,
      sameAsFields: [
        CONTACT_SAME_AS_FIELDS.sameAsInvoiceDelivery,
        CONTACT_SAME_AS_FIELDS.sameAsCoa,
        CONTACT_SAME_AS_FIELDS.sameAsPackingList
      ]
    },
    {
      key: CARD_CATEGORIES.additionalAttachmentCard,
      Component: AdditionalAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.additionalAttachment }]
    },
    {
      key: CARD_CATEGORIES.privacyPolicy,
      Component: PrivacyPolicyCard
    }
  ]
};

const reviewPageTemplate: PageTemplate = {
  route: PAGE_ROUTES.review,
  Component: ReviewPage,
  accordions: [
    {
      title: 'tabTitles.company',
      cards: companyPageTemplate.cards!
    }
  ]
};

const pages: PageTemplate[] = [companyPageTemplate, reviewPageTemplate];

export default pages;